/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Journeys (contract section 5): where sessions start and end, the most common moves between screens, what came
 * before and after a focus screen, and the top three-step paths, from GET /v1/admin/usage/journeys?focus= with the lens.
 * The focus is picked from every screen the answer names (and the catalog's screens), and sent back to the service.
 */
import { useState } from 'react';
import { Route } from 'lucide-react';
import { api } from '../../lib/api';
import { journeyScreens, nameLabel, normalizeCatalog, normalizeJourneys, type JourneyPath, type Transition } from '../../lib/usage';
import { EmptyState, Grid, Panel, Select, Stack, Table, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { NamedBars, UsageError, type SectionProps } from './parts';

export function UsageJourneys({ query, lensKey }: SectionProps) {
  const [focus, setFocus] = useState<string>('');
  const asked = { ...query, focus: focus || undefined };
  const { load, reload, reloading } = useLoad(() => api.usageJourneys(asked).then((wire) => normalizeJourneys(wire, focus || null)), `journeys|${lensKey}|${focus}`);
  const catalog = useLoad(() => api.usageCatalog().then(normalizeCatalog, () => null), 'journeys-catalog');
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';
  const screens = data ? journeyScreens(data, catalog.load.state === 'ready' ? catalog.load.data : null) : focus ? [focus] : [];

  if (load.state === 'failed') return <Panel title="Journeys"><UsageError title="Journeys could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></Panel>;

  const transitionColumns: Array<Column<Transition>> = [
    { key: 'from', header: 'From', cell: (row) => nameLabel(row.from) },
    { key: 'to', header: 'To', cell: (row) => nameLabel(row.to) },
    { key: 'count', header: 'Times', numeric: true, cell: (row) => count(row.count) },
  ];
  const pathColumns: Array<Column<JourneyPath>> = [
    { key: 'path', header: 'Path', cell: (row) => row.steps.map(nameLabel).join(' › ') },
    { key: 'count', header: 'Sessions', numeric: true, cell: (row) => count(row.count) },
  ];
  const empty = data !== null && data.entries.length === 0 && data.exits.length === 0 && data.transitions.length === 0 && data.paths.length === 0 && !data.focus;

  return (
    <Stack>
      <Panel
        title="Focus a screen"
        description="What people did just before and just after one screen"
        actions={(
          <Select size="sm" className="ui-w-md" aria-label="Focus screen" value={focus} onChange={(event) => setFocus(event.target.value)}>
            <option value="">No focus</option>
            {screens.map((name) => <option key={name} value={name}>{nameLabel(name)}</option>)}
          </Select>
        )}
      >
        {loading ? <EmptyState compact title="Loading" /> : data?.focus ? (
          <Grid cols={2}>
            <div className="ui-cell-stack">
              <h3 className="ui-section-title">Before {nameLabel(data.focus.name)}</h3>
              <NamedBars rows={data.focus.previous} label="Previous screen" empty={<EmptyState compact title="Nothing before it">Sessions opened on this screen, or the window holds no moves to it.</EmptyState>} onPick={setFocus} />
            </div>
            <div className="ui-cell-stack">
              <h3 className="ui-section-title">After {nameLabel(data.focus.name)}</h3>
              <NamedBars rows={data.focus.next} label="Next screen" empty={<EmptyState compact title="Nothing after it">Sessions ended on this screen, or the window holds no moves from it.</EmptyState>} onPick={setFocus} />
            </div>
          </Grid>
        ) : <EmptyState compact icon={Route} title="Pick a screen">Choose a screen above to see what came before and after it.</EmptyState>}
      </Panel>
      {empty ? (
        <Panel title="Entry and exit screens">
          <EmptyState compact icon={Route} title="No journeys in this window">Moves between screens appear here as people use Gyro; data starts at the client release, and the lens above may be leaving people out.</EmptyState>
        </Panel>
      ) : (
        <>
          <Grid cols={2}>
            <Panel title="Entry screens" description="Where sessions begin">
              {loading ? <EmptyState compact title="Loading" /> : <NamedBars rows={data?.entries ?? []} label="Sessions starting on" empty={<EmptyState compact title="No entry screens">Nothing in this window.</EmptyState>} onPick={setFocus} />}
            </Panel>
            <Panel title="Exit screens" description="Where sessions end">
              {loading ? <EmptyState compact title="Loading" /> : <NamedBars rows={data?.exits ?? []} label="Sessions ending on" empty={<EmptyState compact title="No exit screens">Nothing in this window.</EmptyState>} onPick={setFocus} />}
            </Panel>
          </Grid>
          <Grid cols={2}>
            <Panel flush title="Top transitions" description="The most common moves from one screen to the next">
              <Table label="Top transitions" columns={transitionColumns} rows={(data?.transitions ?? []).slice(0, 20)} rowKey={(row) => `${row.from}>${row.to}`} loading={loading} skeletonRows={5} stickyHeader={false} onRowClick={(row) => setFocus(row.to)} empty={<EmptyState compact title="No transitions">Nothing in this window.</EmptyState>} />
            </Panel>
            <Panel flush title="Top paths" description="The most common three screens in a row">
              <Table label="Top paths" columns={pathColumns} rows={(data?.paths ?? []).slice(0, 20)} rowKey={(row) => row.steps.join('>')} loading={loading} skeletonRows={5} stickyHeader={false} empty={<EmptyState compact title="No paths">Three-step paths appear once sessions visit three screens in a row.</EmptyState>} />
            </Panel>
          </Grid>
        </>
      )}
    </Stack>
  );
}
